import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Confirmation } from '~/components';
import { useApi, useConfirmation, useSession, useToast, useWorkspace } from '~/contexts';
import { colors } from '~/styles';
import ImportedResults from './ImportedResults';
import LatestSync from './LatestSync';
import ManualSyncModal from './ManualSyncModal';
import SyncResults from './SyncResults';
import SyncSettingsDrawer from './SyncSettingsDrawer';

const Blocks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;
  margin: -1rem;
  margin-top: 1rem;
`;

const Block = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: calc(50% - 2rem);
  margin: 1rem;
  padding: 1.5rem;
  background-color: ${colors.white};
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
  border-radius: 0.3125rem;
`;

const FullBlock = styled(Block)`
  width: calc(100% - 2rem);
`;

const IMPORTED_REFRESH_SECONDS = 2;

export default function HarvestSettings({ integration, reloadIntegration }) {
  const api = useApi();
  const confirmation = useConfirmation();
  const toast = useToast();
  const { isAdmin } = useSession();
  const { workspace } = useWorkspace();
  const [showManualSync, setShowManualSync] = useState(false);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [forceRefreshSyncsDate, setForceRefreshSyncsDate] = useState(moment().format());
  const [resultsData, setResultsData] = useState({});
  const [isResultsLoading, setIsResultsLoading] = useState(true);
  const [hasSync, setHasSync] = useState(
    Boolean(integration.settings.mostRecentSync) || integration.settings.hasMigratedData,
  );
  const [isRunning, setIsRunning] = useState(false);
  const wasRunning = useRef(false);

  const fetchResults = useCallback(async () => {
    setIsResultsLoading(true);
    try {
      const { data } = await api.www.workspaces(workspace.id).harvest.getResults();
      setResultsData(data);
    } finally {
      setIsResultsLoading(false);
    }
  }, [api, workspace]);

  const handleSync = useCallback(
    (sync) => {
      const running = Boolean(sync?.isRunning);
      setHasSync(Boolean(sync) || integration.settings.hasMigratedData);
      setIsRunning(running);
      if (wasRunning.current && !running) {
        fetchResults();
        reloadIntegration();
      }
      wasRunning.current = running;
    },
    [fetchResults, reloadIntegration, integration.settings.hasMigratedData],
  );

  const startSync = async () => {
    if (isAdmin) {
      setShowManualSync(true);
      return;
    }

    const fullSync = !integration.settings.canRunIncremental;
    const confirmed = await confirmation.prompt((resolve) => (
      <Confirmation
        resolve={resolve}
        title={fullSync ? 'Start Migration' : 'Update from Harvest'}
        label={fullSync ? 'Start Migration' : 'Update'}>
        {fullSync
          ? "We'll copy your data from Harvest into Ruddr. It runs in the background, so you can leave this page."
          : "We'll bring over anything that changed in Harvest since your last migration. It runs in the background."}
      </Confirmation>
    ));

    if (!confirmed) {
      return;
    }

    try {
      await api.www.workspaces(workspace.id).harvest.manualSync({ fullSync });
      setForceRefreshSyncsDate(moment().format());
    } catch ({ message }) {
      toast.error(message || 'There was a problem completing your request. Please try again later.');
    }
  };

  useEffect(() => {
    fetchResults();
  }, [fetchResults, forceRefreshSyncsDate]);

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }
    const interval = setInterval(async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).harvest.getImportedResults();
        setResultsData((current) => ({
          ...current,
          results: current.results?.map((row) => ({
            ...row,
            total: data.results.find((imported) => imported.name === row.name)?.total ?? row.total,
          })),
        }));
      } catch {
        return;
      }
    }, IMPORTED_REFRESH_SECONDS * 1000);
    return () => clearInterval(interval);
  }, [isRunning, api, workspace]);

  return (
    integration && (
      <>
        <Blocks>
          <FullBlock>
            <LatestSync
              integration={integration}
              refreshDate={forceRefreshSyncsDate}
              results={resultsData.results}
              estimate={resultsData.estimate}
              onNewSync={startSync}
              onSync={handleSync}
              onChangeSettings={() => setShowSyncSettings(true)}
            />
          </FullBlock>

          {hasSync && (
            <>
              <FullBlock>
                <ImportedResults
                  results={resultsData.results}
                  incremental={resultsData.incremental}
                  isLoading={isResultsLoading}
                  isRunning={isRunning}
                  refreshSeconds={IMPORTED_REFRESH_SECONDS}
                  onRefresh={fetchResults}
                />
              </FullBlock>
              <FullBlock>
                <SyncResults refreshDate={forceRefreshSyncsDate} />
              </FullBlock>
            </>
          )}
        </Blocks>
        {showManualSync && (
          <ManualSyncModal
            integration={integration}
            onClose={() => setShowManualSync(false)}
            onFinish={() => setForceRefreshSyncsDate(moment().format())}
          />
        )}
        {showSyncSettings && (
          <SyncSettingsDrawer
            integration={integration}
            onClose={() => setShowSyncSettings(false)}
            onSave={() => reloadIntegration()}
          />
        )}
      </>
    )
  );
}
